/**
 * Safety Scans workspace/register grouping.
 * Authority is finding_id (Case). Current Scan comes from backend current-Scan identity.
 */

import { safetyScanVerifyPath } from '../acceptance/scanVerifyNavigation';
import { NO_COMPLETED_SAFETY_SCAN_YET as NO_COMPLETED_SAFETY_SCAN_YET_COPY } from './safetyCasesWorkspacePresentationCopy';
import { caseRegisterIssueDisplay, type CaseRegisterRow, type ScanRegisterRow } from './workspaceRegisters';
import { AD_HOC_CASE_GROUP_LABEL, isAdHocScanRow } from './workspaceScansOrganize';
import { isEmptyRegisterValue } from './workspaceStatusDimensions';

export const AD_HOC_FINDING_KEY = '__ad_hoc__';
export const NO_COMPLETED_SAFETY_SCAN_YET = NO_COMPLETED_SAFETY_SCAN_YET_COPY;

export type CaseScanGroup = {
  findingId: string;
  isAdHoc: boolean;
  title: string;
  siteArea: string;
  caseState: string;
  completedScanCount: number;
  caseRow: CaseRegisterRow | null;
  current: ScanRegisterRow | null;
  history: ScanRegisterRow[];
  latestSigned: ScanRegisterRow | null;
};

function trimId(value: unknown): string {
  return String(value || '').trim();
}

function dateTimestamp(value?: string | null): number {
  const ms = new Date(String(value || '')).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** Grouping authority is finding_id. Never workflow_id, camera_id, title, or policy name. */
export function scanFindingId(row: Pick<ScanRegisterRow, 'caseId'>): string {
  return trimId(row.caseId);
}

export function caseFindingId(row: Pick<CaseRegisterRow, 'caseId'>): string {
  return trimId(row.caseId);
}

export function scanVerifyHref(row: Pick<ScanRegisterRow, 'reportId'>): string {
  const reportId = trimId(row.reportId);
  return reportId ? safetyScanVerifyPath(reportId) : '';
}

export function canOpenScan(row: Pick<ScanRegisterRow, 'reportId'> | null | undefined): boolean {
  return Boolean(trimId(row?.reportId));
}

function managedCaseTitleFromRelated(relatedCase: string): string {
  const related = String(relatedCase || '').trim();
  const stripped = related.replace(/^Managed Case:\s*/i, '').trim();
  if (!stripped || stripped.toLowerCase() === 'not created' || isEmptyRegisterValue(stripped)) {
    return AD_HOC_CASE_GROUP_LABEL;
  }
  return stripped;
}

function compareHistoryRecency(left: ScanRegisterRow, right: ScanRegisterRow): number {
  const timeCmp = dateTimestamp(right.completedAt) - dateTimestamp(left.completedAt);
  if (timeCmp !== 0) return timeCmp;
  return String(right.id).localeCompare(String(left.id));
}

function compareHistoryOldestFirst(left: ScanRegisterRow, right: ScanRegisterRow): number {
  const timeCmp = dateTimestamp(left.completedAt) - dateTimestamp(right.completedAt);
  if (timeCmp !== 0) return timeCmp;
  return String(left.id).localeCompare(String(right.id));
}

function collapseLogicalScanRevisions(
  rows: ScanRegisterRow[],
  preferredReportId: string | null,
): ScanRegisterRow[] {
  const buckets = new Map<string, ScanRegisterRow[]>();
  for (const row of rows) {
    const key = String(row.logicalScanKey || row.reportId || row.id).trim() || row.id;
    const list = buckets.get(key);
    if (list) list.push(row);
    else buckets.set(key, [row]);
  }
  const collapsed: ScanRegisterRow[] = [];
  for (const peers of Array.from(buckets.values())) {
    const preferred = preferredReportId
      ? peers.find((row) => trimId(row.reportId) === preferredReportId)
      : null;
    if (preferred) {
      collapsed.push(preferred);
      continue;
    }
    const ordered = [...peers].sort(compareHistoryRecency);
    if (ordered[0]) collapsed.push(ordered[0]);
  }
  return collapsed;
}

function headerFromCase(row: CaseRegisterRow): Pick<CaseScanGroup, 'title' | 'siteArea' | 'caseState'> {
  const issue = caseRegisterIssueDisplay(row);
  return {
    title: issue.issue,
    siteArea: String(row.siteArea || '').trim() || '—',
    caseState: String(row.controlStage || '').trim() || '—',
  };
}

function headerFromScans(rows: ScanRegisterRow[], isAdHoc: boolean): Pick<CaseScanGroup, 'title' | 'siteArea' | 'caseState'> {
  const sample = rows[0];
  if (!sample || isAdHoc) {
    return { title: AD_HOC_CASE_GROUP_LABEL, siteArea: sample?.siteZone || '—', caseState: '—' };
  }
  return {
    title: managedCaseTitleFromRelated(sample.relatedCase),
    siteArea: String(sample.siteZone || '').trim() || '—',
    caseState: '—',
  };
}

function latestSignedFromCase(
  caseRow: CaseRegisterRow | null,
  scans: ScanRegisterRow[],
): ScanRegisterRow | null {
  const reportId = trimId(caseRow?.latestSignedScanLink?.reportId);
  if (!reportId) return null;
  return scans.find((row) => trimId(row.reportId) === reportId) || null;
}

export function groupScansUnderCases(args: {
  scanRows: ScanRegisterRow[];
  caseRows?: CaseRegisterRow[];
  includeZeroScanCases?: boolean;
}): CaseScanGroup[] {
  const scanRows = args.scanRows || [];
  const caseRows = args.caseRows || [];
  const includeZeroScanCases = Boolean(args.includeZeroScanCases);
  const casesByFinding = new Map<string, CaseRegisterRow>();
  for (const row of caseRows) {
    const findingId = caseFindingId(row);
    if (!findingId) continue;
    const existing = casesByFinding.get(findingId);
    if (!existing) {
      casesByFinding.set(findingId, row);
      continue;
    }
    if (dateTimestamp(row.updatedAt) > dateTimestamp(existing.updatedAt)) {
      casesByFinding.set(findingId, row);
    }
  }

  const scansByFinding = new Map<string, ScanRegisterRow[]>();
  for (const row of scanRows) {
    const findingId = scanFindingId(row) || AD_HOC_FINDING_KEY;
    const list = scansByFinding.get(findingId);
    if (list) list.push(row);
    else scansByFinding.set(findingId, [row]);
  }

  if (includeZeroScanCases) {
    for (const findingId of Array.from(casesByFinding.keys())) {
      if (!scansByFinding.has(findingId)) scansByFinding.set(findingId, []);
    }
  }

  const groups: CaseScanGroup[] = [];
  for (const [findingId, rawScans] of Array.from(scansByFinding.entries())) {
    const isAdHoc = findingId === AD_HOC_FINDING_KEY || rawScans.some((row) => isAdHocScanRow(row) && !scanFindingId(row));
    const caseRow = isAdHoc ? null : (casesByFinding.get(findingId) || null);
    const currentReportId = trimId(caseRow?.currentScanLink?.reportId) || null;
    const collapsed = collapseLogicalScanRevisions(rawScans, currentReportId);
    const current = currentReportId
      ? (collapsed.find((row) => trimId(row.reportId) === currentReportId) || null)
      : null;
    const history = collapsed
      .filter((row) => row !== current)
      .sort(compareHistoryRecency);
    const header = caseRow ? headerFromCase(caseRow) : headerFromScans(collapsed, isAdHoc);
    const completedScanCount = collapsed.length;
    groups.push({
      findingId,
      isAdHoc,
      title: header.title,
      siteArea: header.siteArea,
      caseState: header.caseState,
      completedScanCount,
      caseRow,
      current,
      history,
      latestSigned: latestSignedFromCase(caseRow, collapsed),
    });
  }

  groups.sort((left, right) => {
    if (left.isAdHoc !== right.isAdHoc) return left.isAdHoc ? 1 : -1;
    const titleCmp = left.title.localeCompare(right.title, undefined, { sensitivity: 'base' });
    if (titleCmp !== 0) return titleCmp;
    return left.findingId.localeCompare(right.findingId);
  });
  return groups;
}

export function firstHistoricalScan(group: CaseScanGroup): ScanRegisterRow | null {
  if (group.completedScanCount <= 1) return null;
  const pool = [...group.history, group.current].filter((row): row is ScanRegisterRow => Boolean(row));
  const oldest = [...pool].sort(compareHistoryOldestFirst)[0] || null;
  if (!oldest || oldest === group.current) return null;
  return oldest;
}
